import { supabase } from '../utils/supabase';
import { normalizeSeriesName } from '../utils/sessionSeries';

const SERIES_SELECT = 'id, club_id, name, description, is_active, created_by, created_at, updated_at';

const mapSeriesError = (error, fallback) => {
  if (error?.code === '23505') {
    return new Error('A series with this name already exists in this club.');
  }
  return new Error(error?.message || fallback);
};

const getCurrentPlayerId = async () => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('players')
    .select('id')
    .eq('auth_id', user.id)
    .maybeSingle();
  return data?.id || null;
};

export const listSessionSeries = async (clubId, { includeInactive = false } = {}) => {
  if (!clubId) return [];

  let query = supabase
    .from('session_series')
    .select(SERIES_SELECT)
    .eq('club_id', clubId)
    .order('name');

  if (!includeInactive) {
    query = query.eq('is_active', true);
  }

  const { data, error } = await query;
  if (error) {
    console.error('Error listing session series:', error);
    throw mapSeriesError(error, 'Failed to load session series');
  }
  return data || [];
};

export const getSessionSeries = async (seriesId) => {
  if (!seriesId) return null;
  const { data, error } = await supabase
    .from('session_series')
    .select(SERIES_SELECT)
    .eq('id', seriesId)
    .maybeSingle();
  if (error) {
    console.error('Error fetching session series:', error);
    throw mapSeriesError(error, 'Failed to load session series');
  }
  return data || null;
};

export const createSessionSeries = async ({ clubId, name, description = null, createdBy } = {}) => {
  const trimmed = normalizeSeriesName(name);
  if (!clubId) throw new Error('Club is required to create a series.');
  if (trimmed.length < 2) throw new Error('Series name must be at least 2 characters.');

  const created_by = createdBy || (await getCurrentPlayerId());
  const { data, error } = await supabase
    .from('session_series')
    .insert({
      club_id: clubId,
      name: trimmed,
      description: description ? String(description).trim() || null : null,
      created_by,
      is_active: true,
    })
    .select(SERIES_SELECT)
    .single();

  if (error) {
    console.error('Error creating session series:', error);
    throw mapSeriesError(error, 'Failed to create session series');
  }
  return data;
};

export const updateSessionSeries = async (seriesId, updates = {}) => {
  if (!seriesId) throw new Error('Series is required.');

  const payload = { updated_at: new Date().toISOString() };
  if (updates.name !== undefined) {
    const trimmed = normalizeSeriesName(updates.name);
    if (trimmed.length < 2) throw new Error('Series name must be at least 2 characters.');
    payload.name = trimmed;
  }
  if (updates.description !== undefined) {
    payload.description = updates.description ? String(updates.description).trim() || null : null;
  }
  if (updates.is_active !== undefined) {
    payload.is_active = Boolean(updates.is_active);
  }

  const { data, error } = await supabase
    .from('session_series')
    .update(payload)
    .eq('id', seriesId)
    .select(SERIES_SELECT)
    .single();

  if (error) {
    console.error('Error updating session series:', error);
    throw mapSeriesError(error, 'Failed to update session series');
  }
  return data;
};

export const archiveSessionSeries = async (seriesId) =>
  updateSessionSeries(seriesId, { is_active: false });
